import classnames from 'classnames';
import React from 'react';

import { useEqualHeightContextStrict } from './EqualHeightContext';
import { ImagePlaceholder, ImagePlaceholderProps } from './ImagePlaceholder';
import { Breakpoint } from './interfaces';
import { useWindowSize } from './useWindowSize';
import { getBreakpointValue, renderUI, uiClassName, wait } from './util';

export interface ImageThronProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width'> {
  alt?: string;
  aspectRatio?: number | ResponsiveSize;
  className?: string;
  fluid?: boolean;
  placeHolder?: ImagePlaceholderProps;
  // sembrerebbe inutile, valutare se toglierla
  quality?: number | ResponsiveSize;
  src?: string | ResponsiveSrc;
  title?: string;
}

export type ResponsiveSrc = Partial<Record<Breakpoint, string>>;
//export type DevicePixelRatio = Partial<Record<Breakpoint, 1 | 2 | 3 | 4>>;
export type ResponsiveSize = Partial<Record<Breakpoint, number>>;

export const ImageThron = ({
  alt,
  aspectRatio,
  className,
  fluid,
  onLoad,
  placeHolder,
  quality,
  src,
  ...otherProps
}: ImageThronProps) => {
  const sourcesBreakpoint: Array<keyof ResponsiveSrc> = ['xxl', 'xl', 'lg', 'md', 'sm'];
  const windowSize = useWindowSize();
  const parentRef = React.useRef<HTMLDivElement>(null);
  const [parentWidth, setParentWidth] = React.useState<number>();
  const [loaded, setLoaded] = React.useState<boolean>(false);

  React.useEffect(() => {
    const _parentWidth = parentRef.current?.clientWidth;
    setParentWidth(_parentWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSize]);

  const getThronImageSrc = (
    _path: string,
    _width?: number,
    _height?: number,
    _dpr?: number,
    _quality?: number
  ): string => {
    const url = new URL(_path);
    url.searchParams.set('crop', 'centered');

    if (_width && _width > 0) {
      url.searchParams.set('w', _width.toString());
    }
    if (_height && _height > 0) {
      url.searchParams.set('h', _height.toString());
    }
    if (_dpr) {
      url.searchParams.set('dpr', _dpr.toString());
    }
    if (_quality && _quality >= 5 && _quality <= 95) {
      url.searchParams.set('q', _quality.toString());
    }
    return url.toString();
  };

  const getAspectRatioFromBreakpoint = React.useCallback(
    (breakpoint: keyof ResponsiveSize) => {
      let _aspectRatio: number | undefined;

      if (typeof aspectRatio === 'number' && aspectRatio > 0) {
        _aspectRatio = aspectRatio;
      } else if (typeof aspectRatio === 'object') {
        _aspectRatio = aspectRatio[breakpoint];
      }

      return _aspectRatio;
    },
    [aspectRatio]
  );

  const getHeightFromBreakpoint = React.useCallback(
    (breakpoint: keyof ResponsiveSize) => {
      const _width = parentWidth;
      const _ratio = getAspectRatioFromBreakpoint(breakpoint);

      return _width && _ratio && Math.floor(_width / _ratio);
    },
    [getAspectRatioFromBreakpoint, parentWidth]
  );

  const getQualityFromBreakpoint = React.useCallback(
    (breakpoint: keyof ResponsiveSize) => {
      let q: number | undefined;

      if (typeof quality === 'number' && quality > 0) {
        q = quality;
      } else if (typeof quality === 'object') {
        q = quality[breakpoint];
      }
      return q;
    },
    [quality]
  );

  const getThronImageFromBreakpoint = React.useCallback(
    (breakpoint: keyof ResponsiveSrc, dpr = 2) => {
      const _width = parentWidth;
      const _height = getHeightFromBreakpoint(breakpoint);
      const _quality = getQualityFromBreakpoint(breakpoint);
      let breakpointSrc: string | undefined;
      if (typeof src === 'string') {
        breakpointSrc = src;
      } else if (typeof src === 'object') {
        breakpointSrc = src[breakpoint];
      }

      return breakpointSrc && getThronImageSrc(breakpointSrc, _width, _height, dpr, _quality);
    },
    [getHeightFromBreakpoint, getQualityFromBreakpoint, parentWidth, src]
  );

  const getThronSrcSetFromBreakpoint = React.useCallback(
    (breakpoint: keyof ResponsiveSrc) => {
      const devicePixelRatio = [1, 2];
      return devicePixelRatio.map((dpr) => `${getThronImageFromBreakpoint(breakpoint, dpr)} ${dpr}x`).join(', ');
    },
    [getThronImageFromBreakpoint]
  );

  const equalHeightContext = useEqualHeightContextStrict();

  const handleLoad = React.useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setLoaded(true);
      wait(100).then(() => {
        equalHeightContext && equalHeightContext.updateTrigger();
        onLoad && onLoad(event);
      });
    },
    [equalHeightContext, onLoad]
  );

  return (
    <>
      {aspectRatio && !loaded && (
        <ImagePlaceholder aspectRatio={aspectRatio} className="img-placeholder" label="" {...placeHolder} />
      )}
      <div ref={parentRef} style={{ overflow: 'hidden', width: '100%' }}>
        <picture className={classnames({ [uiClassName({ bs5: 'd-none' })]: !loaded })}>
          {sourcesBreakpoint.map((sourceBreakpoint) => {
            return (
              getThronImageFromBreakpoint(sourceBreakpoint) && (
                <source
                  key={sourceBreakpoint}
                  media={`(min-width: ${getBreakpointValue(sourceBreakpoint)}px)`}
                  srcSet={getThronSrcSetFromBreakpoint(sourceBreakpoint)}
                />
              )
            );
          })}
          {renderUI({
            bs5: (
              // eslint-disable-next-line react/forbid-elements, jsx-a11y/alt-text
              <img
                {...otherProps}
                alt={alt}
                className={classnames({ 'img-fluid': fluid }, className)}
                // height={getHeightFromBreakpoint('xs')}
                onLoad={handleLoad}
                src={getThronImageFromBreakpoint('xs')}
                srcSet={getThronSrcSetFromBreakpoint('xs')}
                style={otherProps.style ? otherProps.style : { height: 'auto' }}
                width={parentWidth}
              />
            ),
          })}
        </picture>
      </div>
    </>
  );
};
